import { useSearchParams } from "react-router-dom";

type ParamValue = string | number | undefined | null;

interface IUseQueryParams {
  /** Current value of a query param, or undefined when absent. */
  get: (key: string) => string | undefined;
  /**
   * Merge changes into the query string (history `replace`). Pass `undefined`/`null`/`""` to drop a
   * param — keeps URLs clean (defaults stay out of the URL).
   */
  set: (changes: Record<string, ParamValue>) => void;
}

/**
 * Thin wrapper around `useSearchParams` for URL-as-source-of-truth list state (page + filters),
 * shared by the sync dashboard and the products overview. Uses `replace` so paging/filtering
 * doesn't flood the history stack.
 */
export const useQueryParams = (): IUseQueryParams => {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = (key: string): string | undefined => searchParams.get(key) ?? undefined;

  const set = (changes: Record<string, ParamValue>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace: true });
  };

  return { get, set };
};
